// SpendApprovals (ticket 167) — super-admin queue for the spend gate. New
// workspaces cannot use AI, texting, email or e-signature until approved here.
// GET /super/spend-approvals?status=…, POST /super/accounts/{id}/spend-approval.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldCheck, Clock, Ban, CheckCircle2, CircleDashed, Copy, Check } from 'lucide-react';
import { api } from '../api';
import { toast } from '../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { formatDate } from '../kit/time';
import { formatPhone } from '../shared/phone';
import { copyText } from '../clipboard';
import { Panel, Loading, ErrorBox } from './panels';

const TABS = [
  { status: 'pending', label: 'Pending' },
  { status: 'denied', label: 'Denied' },
  { status: 'approved', label: 'Approved' },
];
const EMPTY_TEXT = {
  pending: 'No workspaces are waiting for approval.',
  denied: 'No denied workspaces.',
  approved: 'No approved workspaces yet.',
};
const LICENCE_LABELS = { nmls: 'NMLS ID', real_estate: 'Real-estate licence' };
// The public register a super-admin checks the NMLS ID against by hand.
const NMLS_LOOKUP_URL = 'https://www.nmlsconsumeraccess.org/';

// Status colours are reserved for state and always ship with an icon + label.
// `not_requested` is display-only: the tenant is `pending` with no form sent yet.
const STATUS = {
  not_requested: { label: 'Not requested', icon: CircleDashed, bg: '#F1F5F9', color: '#475569' },
  pending: { label: 'Pending', icon: Clock, bg: '#FEF3C7', color: '#92400E' },
  denied: { label: 'Denied', icon: Ban, bg: '#FEE2E2', color: '#991B1B' },
  approved: { label: 'Approved', icon: CheckCircle2, bg: '#DCFCE7', color: '#166534' },
};

function StatusChip({ status }) {
  const s = STATUS[status];
  if (!s) return null;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: s.bg, color: s.color }} data-testid={`spend-status-${status}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

// A value the super-admin pastes into the licence register: click to copy.
function CopyValue({ value, label, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!(await copyText(value))) {
      toast({ title: 'Could not copy', description: 'Select the text and copy it by hand.' });
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const Icon = copied ? Check : Copy;
  return (
    <button type="button" onClick={copy} data-testid={testid} aria-label={`Copy ${label}`}
      title={copied ? 'Copied' : `Copy ${label}`}
      className="inline-flex items-center gap-1.5 max-w-full text-left font-semibold text-slate-800 rounded px-1 -mx-1 hover:bg-slate-100">
      <span className="break-words min-w-0">{value}</span>
      <Icon size={12} className={`shrink-0 ${copied ? 'text-green-700' : 'text-slate-400'}`} />
    </button>
  );
}

const btn = 'text-[12px] font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50';

function Row({ item, busy, onAct }) {
  const r = item.request;
  const id = item.tenant_id;
  const st = item.spend_approval;
  return (
    <li className="px-5 py-3 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`spend-approval-row-${id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold" style={{ color: '#070B5F' }}>{item.name}</span>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600" data-testid={`spend-kind-${id}`}>
            {item.kind === 'personal' ? 'Personal' : 'Company'}
          </span>
          <StatusChip status={st === 'pending' && !r ? 'not_requested' : st} />
        </div>
        <div className="text-[12px] text-slate-600 mt-0.5 break-words">
          {item.owner_email || 'No owner'} · Created {item.created_at ? formatDate(item.created_at) : 'unknown'}
        </div>
        {r ? (
          <div className="text-[12px] text-slate-700 mt-1 break-words" data-testid={`spend-request-${id}`}>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 items-baseline">
              <dt className="text-slate-500">Company</dt>
              <dd><CopyValue value={r.company_name} label="company name" testid={`spend-copy-company-${id}`} /></dd>
              <dt className="text-slate-500">{LICENCE_LABELS[r.licence_type] || r.licence_type}</dt>
              <dd><CopyValue value={r.licence_number} label="licence number" testid={`spend-copy-licence-${id}`} /></dd>
              <dt className="text-slate-500">State</dt>
              <dd>{r.licence_state}</dd>
              <dt className="text-slate-500">Phone</dt>
              <dd>{formatPhone(r.phone)}</dd>
            </dl>
            <div className="text-[11px] text-slate-500 mt-1">
              Submitted {r.submitted_at ? formatDate(r.submitted_at) : 'unknown'}{r.submitted_by_email ? ` by ${r.submitted_by_email}` : ''}
            </div>
            {r.licence_type === 'nmls' && (
              <a href={NMLS_LOOKUP_URL} target="_blank" rel="noopener noreferrer"
                className="text-[11px] font-semibold text-slate-600 underline hover:text-slate-900"
                data-testid={`spend-nmls-lookup-${id}`}>
                Look up on NMLS Consumer Access
              </a>
            )}
          </div>
        ) : (
          <div className="text-[12px] text-slate-400 italic mt-1" data-testid={`spend-no-request-${id}`}>No request submitted</div>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        {(st === 'pending' || st === 'denied') && (
          <button type="button" disabled={busy} onClick={() => onAct(item, 'approve')} data-testid={`spend-approve-${id}`}
            className={`${btn} text-white`} style={{ background: '#6BA82E' }}>Approve</button>
        )}
        {st === 'pending' && (
          <button type="button" disabled={busy} onClick={() => onAct(item, 'deny')} data-testid={`spend-deny-${id}`}
            className={`${btn} border border-red-200 text-red-700 hover:bg-red-50`}>Deny</button>
        )}
        {st === 'approved' && (
          <button type="button" disabled={busy} onClick={() => onAct(item, 'revoke')} data-testid={`spend-revoke-${id}`}
            className={`${btn} border border-red-200 text-red-700 hover:bg-red-50`}>Revoke</button>
        )}
      </div>
    </li>
  );
}

const SUCCESS = { approve: 'Workspace approved', deny: 'Workspace denied', revoke: 'Approval revoked' };
const CONFIRM = {
  deny: (n) => ({ title: `Deny ${n}?`, description: 'They keep CRM and pipeline access but cannot use AI, texting, email or e-signature.', confirmText: 'Deny', destructive: true }),
  revoke: (n) => ({ title: `Revoke approval for ${n}?`, description: 'AI, texting, email and e-signature stop working for this workspace immediately.', confirmText: 'Revoke', destructive: true }),
};

export default function SpendApprovals() {
  const [status, setStatus] = useState('pending');
  const [state, setState] = useState({ items: null, error: null });
  const [busyId, setBusyId] = useState(null);
  const reqSeq = useRef(0);

  const load = useCallback((s) => {
    const my = ++reqSeq.current;
    return api.get(`/super/spend-approvals?status=${s}`)
      .then((r) => { if (my === reqSeq.current) setState({ items: r.data.items || [], error: null }); })
      .catch((e) => { if (my === reqSeq.current) setState({ items: null, error: e?.response?.data?.detail || 'Could not load spend approvals' }); });
  }, []);

  useEffect(() => {
    setState({ items: null, error: null });
    load(status);
  }, [status, load]);

  const act = async (item, action) => {
    if (busyId) return;
    if (CONFIRM[action] && !(await confirmDialog(CONFIRM[action](item.name)))) return;
    setBusyId(item.tenant_id);
    try {
      await api.post(`/super/accounts/${item.tenant_id}/spend-approval`, { action });
      toast({ title: SUCCESS[action] });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast({ title: 'Could not update', description: typeof d === 'string' ? d : (d?.message || 'Try again') });
    } finally {
      setBusyId(null);
    }
    await load(status);
  };

  const { items, error } = state;
  return (
    <Panel
      title="Spend approvals"
      sub="New workspaces cannot use AI, texting, email or e-signature until approved."
      icon={ShieldCheck}
      testid="spend-approvals-panel"
      action={(
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden shrink-0" role="tablist">
          {TABS.map((t) => (
            <button key={t.status} type="button" role="tab" aria-selected={status === t.status}
              onClick={() => setStatus(t.status)} data-testid={`spend-approvals-tab-${t.status}`}
              className={`text-[12px] font-semibold px-3 py-1.5 ${status === t.status ? 'bg-[#070B5F] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
    >
      {error && <div className="p-4"><ErrorBox testid="spend-approvals-error" error={error} /></div>}
      {!error && !items && <div className="p-5"><Loading testid="spend-approvals-loading" label="Loading spend approvals…" /></div>}
      {items && items.length === 0 && (
        <div className="p-6 text-sm text-slate-500 italic" data-testid="spend-approvals-empty">{EMPTY_TEXT[status]}</div>
      )}
      {items && items.length > 0 && (
        <ul className="divide-y divide-slate-50" data-testid="spend-approvals-list">
          {items.map((it) => <Row key={it.tenant_id} item={it} busy={busyId === it.tenant_id} onAct={act} />)}
        </ul>
      )}
    </Panel>
  );
}
